/**
 * Testimonial Slider Custom Element
 * Handles navigation and position swapping animation
 */
class TestimonialSlider extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.isTransitioning = false;
    this.items = [];
    this.dots = [];
    this.prevButton = null;
    this.nextButton = null;
  }

  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.testimonial-slider__item'));
    this.dots = Array.from(this.querySelectorAll('.testimonial-slider__dot'));
    this.prevButton = this.querySelector('[data-action="prev"]');
    this.nextButton = this.querySelector('[data-action="next"]');

    if (this.items.length <= 1) return;

    // Set initial state
    this.updateActiveState();

    // Event listeners
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.previous());
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.next());
    }

    // Dot navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToIndex(index));
    });

    // Listen to prev-next button events from theme
    this.addEventListener('prev-next:prev', () => this.previous());
    this.addEventListener('prev-next:next', () => this.next());
  }

  updateActiveState() {
    // Update items visibility
    this.items.forEach((item, index) => {
      if (index === this.currentIndex) {
        item.classList.remove('is-hidden');
      } else {
        item.classList.add('is-hidden');
      }
    });

    // Update dots
    this.dots.forEach((dot, index) => {
      if (index === this.currentIndex) {
        dot.classList.add('is-active');
      } else {
        dot.classList.remove('is-active');
      }
    });

    // Update button states (allow infinite looping)
    if (this.prevButton) {
      this.prevButton.disabled = false;
    }
    if (this.nextButton) {
      this.nextButton.disabled = false;
    }
  }

  async goToIndex(newIndex, animate = true) {
    if (this.isTransitioning || newIndex === this.currentIndex) return;
    if (newIndex < 0 || newIndex >= this.items.length) return;

    this.isTransitioning = true;

    const currentItem = this.items[this.currentIndex];
    const nextItem = this.items[newIndex];

    // Always swap positions on navigation - toggle the swap state
    if (animate) {
      // Add swapping class for animation
      nextItem.classList.add('is-swapping');
      
      // Toggle swap state on the next item (it will swap positions)
      nextItem.classList.toggle('is-swapped');
    }

    // Hide current item
    currentItem.classList.add('is-hidden');

    // Show next item
    nextItem.classList.remove('is-hidden');

    // Update index
    this.currentIndex = newIndex;

    // Wait for transition
    if (animate) {
      await new Promise(resolve => setTimeout(resolve, 600));
    }

    // Clean up classes
    nextItem.classList.remove('is-swapping');

    this.updateActiveState();
    this.isTransitioning = false;
  }

  previous() {
    const newIndex = (this.currentIndex - 1 + this.items.length) % this.items.length;
    this.goToIndex(newIndex);
  }

  next() {
    const newIndex = (this.currentIndex + 1) % this.items.length;
    this.goToIndex(newIndex);
  }
}

// Register custom element
if (!customElements.get('testimonial-slider')) {
  customElements.define('testimonial-slider', TestimonialSlider);
}

/**
 * Wrap Flickity navigation buttons and pagination dots in a container div
 */
function wrapFlickityNavigation(carousel) {
  // Check if already wrapped
  if (carousel.querySelector('.flickity-navigation-wrapper')) {
    return;
  }

  const prevNextButtons = carousel.querySelectorAll('.flickity-prev-next-button');
  const pageDots = carousel.querySelector('.flickity-page-dots');

  // Only proceed if we have navigation elements
  if (prevNextButtons.length === 0 && !pageDots) {
    return;
  }

  // Create wrapper div
  const wrapper = document.createElement('div');
  wrapper.className = 'flickity-navigation-wrapper';

  // Wrap prev/next buttons
  prevNextButtons.forEach(button => {
    wrapper.appendChild(button);
  });

  // Wrap pagination dots
  if (pageDots) {
    wrapper.appendChild(pageDots);
  }

  // Insert wrapper into carousel
  carousel.appendChild(wrapper);
}

// Listen for Flickity ready events on product media carousels
document.addEventListener('flickity:ready', function(event) {
  const carousel = event.target;
  if (carousel.classList.contains('product__media-list')) {
    // Use setTimeout to ensure Flickity has finished creating all elements
    setTimeout(() => {
      wrapFlickityNavigation(carousel);
    }, 0);
  }
});

// Also handle carousels that might already be initialized
document.addEventListener('DOMContentLoaded', function() {
  // Check for existing carousels after a short delay to allow Flickity to initialize
  setTimeout(() => {
    const productCarousels = document.querySelectorAll('.product__media-list');
    productCarousels.forEach(carousel => {
      wrapFlickityNavigation(carousel);
    });
  }, 100);
});

/**
 * Enable autoplay for product carousels with thumbnails-bottom layout
 */
function initProductCarouselAutoplay() {
  // Check for products with thumbnails-bottom class
  const productsWithBottomThumbnails = document.querySelectorAll('.product.product--thumbnails-bottom');
  
  productsWithBottomThumbnails.forEach(product => {
    const carousel = product.querySelector('flickity-carousel.product__media-list');
    if (!carousel) return;
    
    // Skip if already initialized
    if (carousel.dataset.autoplayInitialized === 'true') return;
    
    // Wait for Flickity to be ready
    const initAutoplay = async () => {
      try {
        // Check if carousel has multiple slides
        if (carousel.childElementCount <= 1) {
          carousel.dataset.autoplayInitialized = 'true';
          return;
        }
        
        // Wait for Flickity instance to be ready
        const flickityInstance = await carousel.flickityInstance;
        if (!flickityInstance || !flickityInstance.slides || flickityInstance.slides.length <= 1) {
          carousel.dataset.autoplayInitialized = 'true';
          return;
        }
        
        // Mark as initialized
        carousel.dataset.autoplayInitialized = 'true';
        
        let autoplayInterval;
        const autoplayDelay = 3000; // 3 seconds
        
        const startAutoplay = () => {
          if (autoplayInterval) return; // Already running
          autoplayInterval = setInterval(() => {
            if (carousel && carousel.next) {
              carousel.next();
            }
          }, autoplayDelay);
        };
        
        const stopAutoplay = () => {
          if (autoplayInterval) {
            clearInterval(autoplayInterval);
            autoplayInterval = null;
          }
        };
        
        // Start autoplay
        startAutoplay();
        
        // Pause on hover
        carousel.addEventListener('mouseenter', stopAutoplay);
        carousel.addEventListener('mouseleave', startAutoplay);
        
        // Pause on user interaction
        carousel.addEventListener('flickity:dragstart', stopAutoplay);
        carousel.addEventListener('flickity:slide-changed', () => {
          // Restart autoplay after slide change (user might have interacted)
          stopAutoplay();
          setTimeout(startAutoplay, 2000);
        });
      } catch (error) {
        // Retry if Flickity not ready yet
        setTimeout(initAutoplay, 100);
      }
    };
    
    // Listen for Flickity ready event
    carousel.addEventListener('flickity:ready', initAutoplay);
    
    // Also try immediately in case it's already ready
    setTimeout(initAutoplay, 300);
  });
}

// Initialize autoplay when DOM is ready
document.addEventListener('DOMContentLoaded', initProductCarouselAutoplay);

// Also listen for dynamically loaded content
document.addEventListener('flickity:ready', function(event) {
  const carousel = event.target;
  if (carousel.tagName === 'FLICKITY-CAROUSEL' && carousel.classList.contains('product__media-list') && carousel.closest('.product.product--thumbnails-bottom')) {
    initProductCarouselAutoplay();
  }
});

/**
 * Initialize product form accordions
 */
function initProductFormAccordions() {
  document.querySelectorAll('.product-form__accordion-header').forEach(header => {
    const collapsibleId = header.getAttribute('data-collapsible');
    const content = document.getElementById(collapsibleId);
    const toggle = header.querySelector('.product-form__accordion-toggle');
    const openByDefaultAttr = header.getAttribute('data-open-by-default');
    const openByDefault = openByDefaultAttr === 'true' || openByDefaultAttr === null; // Default to true if not set
    
    if (!content || !toggle) return;

    // Remove any existing state classes first
    content.classList.remove('is-open');
    toggle.classList.remove('is-closed');

    // Set initial state based on setting
    if (openByDefault) {
      content.classList.add('is-open');
      toggle.classList.remove('is-closed');
    } else {
      content.classList.remove('is-open');
      toggle.classList.add('is-closed');
    }

    header.addEventListener('click', () => {
      const isOpen = content.classList.contains('is-open');
      
      if (isOpen) {
        content.classList.remove('is-open');
        toggle.classList.add('is-closed');
      } else {
        content.classList.add('is-open');
        toggle.classList.remove('is-closed');
      }
    });
  });
}

// Initialize accordions when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductFormAccordions);
} else {
  initProductFormAccordions();
}

// Re-initialize accordions when product form is rerendered
document.addEventListener('product:rerendered', function() {
  setTimeout(initProductFormAccordions, 100);
});

/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */